"use client"

import type React from "react"
import { useState, useCallback, useRef, useEffect } from "react"
import { motion, useMotionValue, useSpring, AnimatePresence } from "framer-motion"

interface TestimonialData {
  quote: string
  author: string
  role: string
  company: string
  avatar: string
}

interface CleanTestimonialProps {
  testimonials: TestimonialData[]
}

function usePreloadImages(images: string[]) {
  useEffect(() => {
    images.forEach((src) => {
      const img = new Image()
      img.src = src
    })
  }, [images])
}

function SplitText({ text }: { text: string }) {
  const words = text.split(" ")

  return (
    <span className="inline">
      {words.map((word, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{
            duration: 0.4,
            delay: i * 0.04,
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
          className="inline-block mr-[0.25em]"
        >
          {word}
        </motion.span>
      ))}
    </span>
  )
}

export function CleanTestimonial({ testimonials }: CleanTestimonialProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  usePreloadImages(testimonials.map((t) => t.avatar))

  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  const springConfig = { damping: 25, stiffness: 150 }
  const cursorX = useSpring(mouseX, springConfig)
  const cursorY = useSpring(mouseY, springConfig)

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      mouseX.set(e.clientX - rect.left)
      mouseY.set(e.clientY - rect.top)
    },
    [mouseX, mouseY],
  )

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length)
  }

  const currentTestimonial = testimonials[activeIndex]

  if (!currentTestimonial) return null

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-4xl mx-auto px-8 py-24 cursor-none select-none"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleNext}
    >
      {/* Custom magnetic cursor */}
      <motion.div
        className="fixed top-0 left-0 pointer-events-none z-50 hidden md:block"
        style={{ x: cursorX, y: cursorY }}
      >
        <motion.div
          className="relative -translate-x-1/2 -translate-y-1/2"
          animate={{
            scale: isHovered ? 1 : 0,
            opacity: isHovered ? 1 : 0,
          }}
          transition={{ duration: 0.3 }}
        >
          <div className="w-20 h-20 rounded-full bg-foreground flex items-center justify-center">
            <span className="text-background text-xs font-medium tracking-wider">Next</span>
          </div>
        </motion.div>
      </motion.div>

      {/* Floating index indicator */}
      <motion.div
        className="absolute top-8 right-8 font-mono text-sm text-muted-foreground"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <span className="text-foreground font-medium">
          {String(activeIndex + 1).padStart(2, "0")}
        </span>
        <span className="mx-1">/</span>
        {String(testimonials.length).padStart(2, "0")}
      </motion.div>

      {/* Stacked avatar previews for other testimonials */}
      <div className="absolute top-8 left-8 flex -space-x-3">
        {testimonials.map((t, i) => (
          <motion.div
            key={i}
            className={`w-10 h-10 rounded-full overflow-hidden border-2 border-background transition-all duration-300 ${
              i === activeIndex ? "ring-2 ring-[hsl(var(--holographic-cyan))] ring-offset-2 ring-offset-background" : "opacity-40"
            }`}
            animate={{ scale: i === activeIndex ? 1.1 : 1 }}
          >
            <img src={t.avatar} alt={t.author} className="w-full h-full object-cover" />
          </motion.div>
        ))}
      </div>

      {/* Main content */}
      <div className="min-h-[400px] flex flex-col justify-center">
        <AnimatePresence mode="wait">
          <motion.blockquote
            key={activeIndex}
            className="text-2xl md:text-4xl lg:text-5xl font-light leading-tight tracking-tight text-foreground mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <span className="text-[hsl(var(--holographic-cyan))]">"</span>
            <SplitText text={currentTestimonial.quote} />
            <span className="text-[hsl(var(--holographic-cyan))]">"</span>
          </motion.blockquote>
        </AnimatePresence>

        {/* Author with reveal line */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              className="flex items-center gap-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              {/* Avatar container with all images stacked */}
              <div className="relative w-16 h-16 rounded-full overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--holographic-cyan))] to-[hsl(var(--holographic-magenta))] opacity-20" />
                {testimonials.map((t, i) => (
                  <motion.img
                    key={i}
                    src={t.avatar}
                    alt={t.author}
                    className="absolute inset-0 w-full h-full object-cover"
                    initial={false}
                    animate={{
                      opacity: i === activeIndex ? 1 : 0,
                      scale: i === activeIndex ? 1 : 1.1,
                    }}
                    transition={{ duration: 0.5 }}
                  />
                ))}
              </div>

              {/* Author info with accent line */}
              <div className="flex items-center gap-4">
                <motion.div
                  className="w-12 h-px bg-gradient-to-r from-[hsl(var(--holographic-cyan))] to-transparent"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                />
                <div>
                  <motion.p
                    className="text-foreground font-medium"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    {currentTestimonial.author}
                  </motion.p>
                  <motion.p
                    className="text-muted-foreground text-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    {currentTestimonial.role} — {currentTestimonial.company}
                  </motion.p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress bar */}
        <div className="absolute bottom-8 left-8 right-8">
          <div className="h-px bg-border overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[hsl(var(--holographic-cyan))] to-[hsl(var(--holographic-magenta))]"
              initial={{ width: "0%" }}
              animate={{ width: `${((activeIndex + 1) / testimonials.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      </div>

      {/* Keyboard hint */}
      <motion.p
        className="absolute bottom-8 right-8 text-xs text-muted-foreground"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 1 }}
      >
        Click anywhere
      </motion.p>
    </div>
  )
}
